import React, { useState, useEffect, useMemo, useCallback } from "react";
import { api, useAuth } from "../App";
import { toast } from "sonner";
import { Plus, ExternalLink, Trash2, X, FileText, AlertTriangle } from "lucide-react";
import AvatarChip from "../components/ui/avatar-chip";
import { EmptyState } from "../components/ui/empty-state";
import UserAutocomplete from "../components/UserAutocomplete";
import useUrlState from "../hooks/useUrlState";

/**
 * Scripts module — link-only submissions, open library.
 *
 * Writers paste a Google Drive / OneDrive link. Anyone signed in can
 * browse, comment via status changes, and walk through scripts to
 * approve / shelve. "Approved" scripts can be promoted into a real
 * production asset later via the existing Production flow — this page
 * only tracks intent.
 *
 * Three tabs: All scripts, My scripts, Daily tracker (writer perf).
 * Submit button → modal with G Drive/OneDrive link permissions reminder.
 */

const STATUS_META = {
  draft:      { label: "Draft",      color: "var(--t3)",   bg: "var(--s2)" },
  submitted:  { label: "Submitted",  color: "#60a5fa",     bg: "rgba(96,165,250,.10)" },
  in_review:  { label: "In review",  color: "#f59e0b",     bg: "rgba(245,158,11,.10)" },
  approved:   { label: "Approved",   color: "#22c55e",     bg: "rgba(34,197,94,.10)" },
  shelved:    { label: "Shelved",    color: "#a78bfa",     bg: "rgba(167,139,250,.10)" },
};

const FORMAT_OPTIONS = [
  { key: "reel",       label: "Reel" },
  { key: "ad_film",    label: "Ad film" },
  { key: "carousel",   label: "Carousel" },
  { key: "long_form",  label: "Long-form video" },
  { key: "other",      label: "Other" },
];

const TABS = [
  { key: "all",   label: "All scripts" },
  { key: "mine",  label: "My scripts" },
  { key: "perf",  label: "Daily tracker" },
];

const STATUS_FILTERS = ["all", "submitted", "in_review", "approved", "shelved", "draft"];

export default function ScriptsPage() {
  const { user } = useAuth();
  const myUid = user?.user_id || user?.id;

  const [tab, setTab] = useUrlState("tab", "all");
  const [statusFilter, setStatusFilter] = useUrlState("status", "all");
  const [scripts, setScripts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [perfData, setPerfData] = useState(null);
  const [perfWindow, setPerfWindow] = useUrlState("win", "30d");
  const [expandedWriter, setExpandedWriter] = useState(null);

  const loadScripts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab === "mine") params.set("mine", "true");
      if (statusFilter !== "all") params.set("status", statusFilter);
      const r = await api.get(`/scripts?${params.toString()}`);
      setScripts(r.data?.items || []);
    } catch (e) {
      toast.error("Couldn't load scripts");
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter]);

  const loadPerf = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/scripts/performance/daily?window=${perfWindow}`);
      setPerfData(r.data);
    } catch (e) {
      toast.error("Couldn't load performance");
    } finally {
      setLoading(false);
    }
  }, [perfWindow]);

  useEffect(() => {
    if (tab === "perf") loadPerf();
    else loadScripts();
  }, [tab, statusFilter, perfWindow, loadScripts, loadPerf]);

  // Accounts for the submit modal — load once.
  useEffect(() => {
    api.get("/accounts").then((r) => {
      const list = r.data || [];
      setAccounts(list.map((a) => ({
        id: a.lead_id || a.account_id || a.id,
        name: a.company_name || a.account_name || a.name || "—",
      })).filter((a) => a.id && a.name));
    }).catch(() => setAccounts([]));
  }, []);

  const activeScript = useMemo(() => scripts.find((s) => s.script_id === activeId), [scripts, activeId]);

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 28px 80px", fontFamily: "var(--font-sans)" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: 22, fontWeight: 600, color: "var(--t1)", letterSpacing: "-0.015em", margin: 0 }}>Scripts</h1>
          <p style={{ color: "var(--t3)", fontSize: 13, marginTop: 4, margin: "4px 0 0" }}>
            Submit script links — anyone on the team can read, review, and route to production.
          </p>
        </div>
        <button
          onClick={() => setSubmitOpen(true)}
          style={{
            padding: "10px 16px",
            background: "var(--t1)",
            color: "var(--bg)",
            border: "none",
            borderRadius: 999,
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Plus size={14} /> Submit a script
        </button>
      </div>

      {/* Tabs */}
      <div className="dm-scripts-tabs" style={{ display: "flex", gap: 6, marginBottom: 16, padding: 4, background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 999, width: "fit-content", maxWidth: "100%", overflowX: "auto" }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: "8px 18px",
              borderRadius: 999,
              border: "none",
              background: tab === t.key ? "var(--s3)" : "transparent",
              color: tab === t.key ? "var(--t1)" : "var(--t3)",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Status filter pills (only in list views) */}
      {tab !== "perf" && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                border: "1px solid var(--bdr)",
                background: statusFilter === s ? "var(--s3)" : "var(--s1)",
                color: statusFilter === s ? "var(--t1)" : "var(--t3)",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                textTransform: "capitalize",
              }}
            >
              {s === "all" ? "All" : (STATUS_META[s]?.label || s.replace("_", " "))}
            </button>
          ))}
        </div>
      )}

      {/* Window pills (only in perf view) */}
      {tab === "perf" && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
          {["7d", "30d", "90d", "all"].map((w) => (
            <button
              key={w}
              onClick={() => setPerfWindow(w)}
              style={{
                padding: "6px 14px",
                borderRadius: 999,
                border: "1px solid var(--bdr)",
                background: perfWindow === w ? "var(--s3)" : "var(--s1)",
                color: perfWindow === w ? "var(--t1)" : "var(--t3)",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {w === "7d" ? "7 days" : w === "30d" ? "30 days" : w === "90d" ? "90 days" : "All time"}
            </button>
          ))}
        </div>
      )}

      {/* Body */}
      {loading && (
        <div style={{ textAlign: "center", padding: 60, color: "var(--t3)" }}>Loading…</div>
      )}

      {/* List view */}
      {!loading && tab !== "perf" && (
        scripts.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No scripts yet"
            message={tab === "mine" ? "You haven't submitted any scripts. Click \"Submit a script\" above." : "Nothing to show with these filters."}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {scripts.map((s) => (
              <ScriptRow key={s.script_id} s={s} onOpen={() => setActiveId(s.script_id)} />
            ))}
          </div>
        )
      )}

      {/* Performance view */}
      {!loading && tab === "perf" && perfData && (
        <PerformanceView
          data={perfData}
          myUid={myUid}
          expandedWriter={expandedWriter}
          setExpandedWriter={setExpandedWriter}
        />
      )}

      {/* Submit modal */}
      {submitOpen && (
        <SubmitModal
          accounts={accounts}
          onClose={() => setSubmitOpen(false)}
          onSubmitted={() => { setSubmitOpen(false); loadScripts(); toast.success("Script submitted"); }}
        />
      )}

      {/* Detail drawer */}
      {activeScript && (
        <DetailDrawer
          script={activeScript}
          myUid={myUid}
          isAdmin={user?.role === "admin" || user?.role === "superadmin"}
          onClose={() => setActiveId(null)}
          onChanged={() => loadScripts()}
        />
      )}
    </div>
  );
}

/* ═══════════════════════ Row ═══════════════════════ */

function ScriptRow({ s, onOpen }) {
  const meta = STATUS_META[s.status] || STATUS_META.submitted;
  const date = s.submitted_at || s.created_at;
  const fmtLabel = FORMAT_OPTIONS.find((f) => f.key === s.format)?.label;
  return (
    <div
      onClick={onOpen}
      style={{
        background: "var(--s1)",
        border: "1px solid var(--bdr)",
        borderRadius: 12,
        padding: "14px 16px",
        cursor: "pointer",
        display: "flex",
        gap: 14,
        alignItems: "center",
      }}
    >
      <AvatarChip name={s.writer_name} size={32} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "var(--t1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {s.title}
          </span>
          <StatusPill status={s.status} />
          {fmtLabel && (
            <span style={{ fontSize: 10, padding: "2px 6px", background: "var(--s3)", color: "var(--t3)", borderRadius: 4, textTransform: "uppercase", letterSpacing: ".04em" }}>
              {fmtLabel}
            </span>
          )}
          {s.doc_total > 1 && (
            <span style={{ fontSize: 10, padding: "2px 6px", background: "rgba(96,165,250,.10)", color: "#60a5fa", borderRadius: 4, fontWeight: 600 }} title={`This doc holds ${s.doc_total} scripts`}>
              {s.doc_index} / {s.doc_total} in doc
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: "var(--t3)", marginTop: 4, display: "flex", flexWrap: "wrap", gap: 8 }}>
          <span>by {s.writer_name}</span>
          {s.account_name && <span>· {s.account_name}</span>}
          {date && <span>· {fmtRelative(date)}</span>}
          {s.reviewer_name && <span>· → {s.reviewer_name}</span>}
        </div>
        {s.logline && (
          <div style={{ fontSize: 13, color: "var(--t2)", marginTop: 6, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            "{s.logline}"
          </div>
        )}
      </div>
      <a
        href={s.link}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: meta.color, padding: "6px 10px", border: "1px solid var(--bdr)", borderRadius: 8, textDecoration: "none" }}
      >
        Open <ExternalLink size={12} />
      </a>
    </div>
  );
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.submitted;
  return (
    <span style={{
      fontSize: 10,
      padding: "2px 8px",
      borderRadius: 999,
      background: meta.bg,
      color: meta.color,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: ".04em",
    }}>
      {meta.label}
    </span>
  );
}

/* ═══════════════════════ Submit modal ═══════════════════════ */

function SubmitModal({ accounts, onClose, onSubmitted }) {
  const MAX_IDEAS = 10;
  const [link, setLink] = useState("");
  const [accountId, setAccountId] = useState("");
  const [format, setFormat] = useState("");
  const [reviewerId, setReviewerId] = useState("");
  const [reviewerName, setReviewerName] = useState("");
  // Each idea: {title, logline}. Always at least one row.
  const [ideas, setIdeas] = useState([{ title: "", logline: "" }]);
  const [submitting, setSubmitting] = useState(false);

  const linkLooksValid = /^https?:\/\/.+/i.test(link.trim());
  const linkLooksDriveOrOneDrive = /(drive\.google\.com|docs\.google\.com|onedrive\.live\.com|sharepoint\.com|1drv\.ms)/i.test(link);
  const validIdeasCount = ideas.filter((i) => (i.title || "").trim()).length;
  const isMulti = ideas.length > 1;

  const setIdeaCount = (n) => {
    const target = Math.max(1, Math.min(MAX_IDEAS, n));
    setIdeas((prev) => {
      if (target === prev.length) return prev;
      if (target > prev.length) {
        return [...prev, ...Array(target - prev.length).fill(0).map(() => ({ title: "", logline: "" }))];
      }
      return prev.slice(0, target);
    });
  };
  const updateIdea = (idx, key, val) => {
    setIdeas((prev) => prev.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  };

  const submit = async () => {
    if (!linkLooksValid) return toast.error("Link must be a full URL (https://…)");
    const cleanIdeas = ideas
      .map((i) => ({ title: (i.title || "").trim(), logline: (i.logline || "").trim() }))
      .filter((i) => i.title);
    if (cleanIdeas.length === 0) return toast.error("At least one title is required");
    setSubmitting(true);
    try {
      const account = accounts.find((a) => a.id === accountId);
      const shared = {
        link: link.trim(),
        account_id: account?.id || null,
        account_name: account?.name || null,
        format: format || null,
        reviewer_user_id: reviewerId || null,
        reviewer_name: reviewerName || null,
        status: "submitted",
      };
      if (cleanIdeas.length === 1) {
        await api.post("/scripts", {
          ...shared,
          title: cleanIdeas[0].title,
          logline: cleanIdeas[0].logline || null,
        });
        toast.success("Script submitted");
      } else {
        await api.post("/scripts/batch", {
          ...shared,
          ideas: cleanIdeas,
        });
        toast.success(`${cleanIdeas.length} scripts submitted`);
      }
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.5)",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      paddingTop: 60, zIndex: 90,
    }}>
      <div onClick={(e) => e.stopPropagation()} className="dm-modal" style={{
        width: "min(560px, 100vw)", background: "var(--s1)", border: "1px solid var(--bdr)",
        borderRadius: 14, padding: 24, maxHeight: "calc(100vh - 80px)", overflowY: "auto",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--t1)", margin: 0 }}>Submit a script</h2>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "var(--t3)", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>

        {/* Permissions reminder — most important UX moment in this whole module */}
        <div style={{
          padding: "10px 12px",
          background: "rgba(245,158,11,.08)",
          border: "1px solid rgba(245,158,11,.3)",
          borderRadius: 10,
          marginBottom: 16,
          display: "flex",
          gap: 10,
          alignItems: "flex-start",
          fontSize: 12,
          color: "var(--t2)",
          lineHeight: 1.5,
        }}>
          <AlertTriangle size={14} style={{ color: "#f59e0b", flexShrink: 0, marginTop: 2 }} />
          <div>
            <strong style={{ color: "var(--t1)" }}>Set your link to "Anyone with the link can view"</strong> before submitting. Otherwise teammates won't be able to read your script.
            <div style={{ color: "var(--t3)", marginTop: 4 }}>
              In Google Drive: Share → Change → Anyone with the link → Viewer.<br/>
              In OneDrive: Share → Anyone with the link → Can view.
            </div>
          </div>
        </div>

        <FormField label="Google Drive / OneDrive link" required hint={!linkLooksValid && link.length > 0 ? "Looks like an incomplete URL" : !linkLooksDriveOrOneDrive && linkLooksValid ? "Heads up: this isn't a Google Drive or OneDrive URL — submit anyway if intentional." : null}>
          <input
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://docs.google.com/document/d/…"
            style={inputStyle}
          />
        </FormField>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <FormField label="Account (optional)">
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)} style={inputStyle}>
              <option value="">— None —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Format (optional)">
            <select value={format} onChange={(e) => setFormat(e.target.value)} style={inputStyle}>
              <option value="">— None —</option>
              {FORMAT_OPTIONS.map((f) => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>
          </FormField>
        </div>

        {/* Idea-count stepper — when >1, the link is shared across N siblings */}
        <FormField
          label="How many scripts in this document?"
          hint={isMulti
            ? `Each idea gets its own status, history, and counts independently in the daily tracker — but they share this link so reviewers open one document.`
            : "If you've packed multiple ideas into one Google Doc, increase this to add a row per idea."}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              onClick={() => setIdeaCount(ideas.length - 1)}
              disabled={ideas.length <= 1}
              style={{ ...stepperBtn, opacity: ideas.length <= 1 ? 0.4 : 1, cursor: ideas.length <= 1 ? "not-allowed" : "pointer" }}
            >−</button>
            <div style={{ minWidth: 80, textAlign: "center", fontSize: 16, fontWeight: 600, color: "var(--t1)" }}>
              {ideas.length} {ideas.length === 1 ? "script" : "scripts"}
            </div>
            <button
              type="button"
              onClick={() => setIdeaCount(ideas.length + 1)}
              disabled={ideas.length >= MAX_IDEAS}
              style={{ ...stepperBtn, opacity: ideas.length >= MAX_IDEAS ? 0.4 : 1, cursor: ideas.length >= MAX_IDEAS ? "not-allowed" : "pointer" }}
            >+</button>
            <span style={{ marginLeft: 8, fontSize: 11, color: "var(--t3)" }}>max {MAX_IDEAS}</span>
          </div>
        </FormField>

        {/* Idea rows */}
        <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 12 }}>
          {ideas.map((idea, idx) => (
            <div key={idx} style={{
              padding: isMulti ? "12px 14px" : "0",
              border: isMulti ? "1px solid var(--bdr)" : "none",
              borderRadius: 10,
              background: isMulti ? "var(--s2)" : "transparent",
            }}>
              {isMulti && (
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase", letterSpacing: ".06em" }}>
                    Idea {idx + 1} of {ideas.length}
                  </div>
                  {ideas.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setIdeas((prev) => prev.filter((_, i) => i !== idx))}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "var(--t3)",
                        cursor: "pointer",
                        fontSize: 11,
                        padding: "4px 8px",
                        borderRadius: 6,
                      }}
                      title="Remove this idea"
                    >Remove</button>
                  )}
                </div>
              )}
              <FormField label={isMulti ? "Title" : "Title"} required>
                <input
                  value={idea.title}
                  onChange={(e) => updateIdea(idx, "title", e.target.value)}
                  placeholder={idx === 0 ? "e.g. EFL — gully cricket reel concept" : `Idea ${idx + 1} title…`}
                  style={inputStyle}
                />
              </FormField>
              <FormField label="Logline / pitch (optional)" hint={idx === 0 ? "One line that hooks a reader before they open the doc" : null}>
                <textarea
                  value={idea.logline}
                  onChange={(e) => updateIdea(idx, "logline", e.target.value)}
                  placeholder="A one-line pitch — what's the hook?"
                  rows={2}
                  style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
                />
              </FormField>
            </div>
          ))}
        </div>

        <FormField label="Tag a reviewer (optional)" hint="If you don't tag anyone, your script lands in an open queue any creative lead can pick up.">
          <UserAutocomplete
            value={reviewerId}
            onChange={(uid, name) => { setReviewerId(uid); setReviewerName(name); }}
            placeholder="Pick a reviewer…"
          />
        </FormField>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          <button
            onClick={submit}
            disabled={submitting || validIdeasCount === 0 || !linkLooksValid}
            style={{ ...btnPrimary, opacity: (submitting || validIdeasCount === 0 || !linkLooksValid) ? 0.5 : 1, cursor: submitting ? "wait" : "pointer" }}
          >
            {submitting
              ? "Submitting…"
              : isMulti
                ? `Submit ${validIdeasCount} script${validIdeasCount === 1 ? "" : "s"}`
                : "Submit script"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, required, hint, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--t3)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6 }}>
        {label}{required && <span style={{ color: "#ef4444", marginLeft: 4 }}>*</span>}
      </label>
      {children}
      {hint && (
        <div style={{ fontSize: 11, color: "var(--t3)", marginTop: 4, lineHeight: 1.5 }}>{hint}</div>
      )}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  background: "var(--s2)",
  border: "1px solid var(--bdr)",
  borderRadius: 8,
  color: "var(--t1)",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

const btnPrimary = {
  padding: "9px 18px",
  background: "var(--t1)",
  color: "var(--bg)",
  border: "none",
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
};

const stepperBtn = {
  width: 32,
  height: 32,
  background: "var(--s2)",
  border: "1px solid var(--bdr)",
  borderRadius: 999,
  color: "var(--t1)",
  fontSize: 16,
  fontWeight: 600,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const btnSecondary = {
  padding: "9px 18px",
  background: "var(--s2)",
  color: "var(--t2)",
  border: "1px solid var(--bdr)",
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};

/* ═══════════════════════ Detail drawer ═══════════════════════ */

function DetailDrawer({ script, myUid, isAdmin, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(script.title || "");
  const [editLink, setEditLink] = useState(script.link || "");
  const [editFormat, setEditFormat] = useState(script.format || "");
  const [editLogline, setEditLogline] = useState(script.logline || "");
  // Allow edits from the writer, the assigned reviewer, and admins. The
  // original gate only allowed writer/admin, which meant teammates couldn't
  // fix typos or add notes on scripts assigned to them for review.
  const canEdit = script.writer_id === myUid || script.reviewer_user_id === myUid || isAdmin;
  const fmtLabel = FORMAT_OPTIONS.find((f) => f.key === script.format)?.label;

  const setStatus = async (newStatus, note) => {
    setBusy(true);
    try {
      await api.put(`/scripts/${script.script_id}/status`, { status: newStatus, note: note || null });
      toast.success(`Marked ${STATUS_META[newStatus]?.label || newStatus}`);
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally {
      setBusy(false);
    }
  };

  const saveEdits = async () => {
    if (!editTitle.trim()) return toast.error("Title is required");
    if (!/^https?:\/\/.+/i.test(editLink.trim())) return toast.error("Link must be a full URL");
    setBusy(true);
    try {
      await api.put(`/scripts/${script.script_id}`, {
        title: editTitle.trim(),
        link: editLink.trim(),
        format: editFormat || null,
        logline: editLogline.trim() || null,
      });
      toast.success("Script updated");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally {
      setBusy(false);
    }
  };

  const cancelEdit = () => {
    setEditTitle(script.title || "");
    setEditLink(script.link || "");
    setEditFormat(script.format || "");
    setEditLogline(script.logline || "");
    setEditing(false);
  };

  const del = async () => {
    if (!window.confirm("Delete this script? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(`/scripts/${script.script_id}`);
      toast.success("Script deleted");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    } finally {
      setBusy(false);
    }
  };

  // What status transitions to show. Keep it simple: any signed-in user can
  // move review forward (in_review / approved / shelved); writer/admin can
  // also move back to draft.
  const nextStatuses = ["in_review", "approved", "shelved"].filter((s) => s !== script.status);

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.5)",
      display: "flex", justifyContent: "flex-end", zIndex: 90,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "min(540px, 100vw)", height: "100vh", background: "var(--s1)",
        borderLeft: "1px solid var(--bdr)", padding: 24, overflowY: "auto",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
              <StatusPill status={script.status} />
              {fmtLabel && (
                <span style={{ fontSize: 10, padding: "2px 6px", background: "var(--s3)", color: "var(--t3)", borderRadius: 4, textTransform: "uppercase", letterSpacing: ".04em" }}>
                  {fmtLabel}
                </span>
              )}
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: "var(--t1)", margin: 0, lineHeight: 1.3 }}>{script.title}</h2>
            <div style={{ fontSize: 12, color: "var(--t3)", marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
              <span>by {script.writer_name}</span>
              {script.account_name && <span>· {script.account_name}</span>}
              {script.submitted_at && <span>· submitted {fmtRelative(script.submitted_at)}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "var(--t3)", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>

        {/* Edit mode — writer / admin can update title, link, format, logline.
            Status changes still go through the status buttons below; this is
            for fixing typos, swapping a broken link, or adding a logline that
            wasn't filled in at submit time. */}
        {editing ? (
          <div style={{ background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 10, padding: 16, marginBottom: 18 }}>
            <FormField label="Title" required>
              <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} style={inputStyle} />
            </FormField>
            <FormField label="Link" required>
              <input value={editLink} onChange={(e) => setEditLink(e.target.value)} style={inputStyle} />
            </FormField>
            <FormField label="Format">
              <select value={editFormat} onChange={(e) => setEditFormat(e.target.value)} style={inputStyle}>
                <option value="">— None —</option>
                {FORMAT_OPTIONS.map((f) => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Logline / pitch">
              <textarea value={editLogline} onChange={(e) => setEditLogline(e.target.value)} rows={2}
                style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }} />
            </FormField>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
              <button onClick={cancelEdit} style={btnSecondary} disabled={busy}>Cancel</button>
              <button onClick={saveEdits} style={{ ...btnPrimary, opacity: busy ? 0.5 : 1, cursor: busy ? "wait" : "pointer" }} disabled={busy}>
                {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        ) : (
          <>
            {script.logline && (
              <div style={{ padding: 12, background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 10, marginBottom: 16, fontSize: 14, fontStyle: "italic", color: "var(--t2)", lineHeight: 1.5 }}>
                "{script.logline}"
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
              <a
                href={script.link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 16px",
                  background: "var(--t1)",
                  color: "var(--bg)",
                  border: "none",
                  borderRadius: 999,
                  textDecoration: "none",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Open script <ExternalLink size={14} />
              </a>
              {canEdit && (
                <button
                  onClick={() => setEditing(true)}
                  style={{
                    padding: "10px 16px",
                    background: "var(--s2)",
                    color: "var(--t2)",
                    border: "1px solid var(--bdr)",
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Edit details
                </button>
              )}
            </div>
          </>
        )}

        {/* Status actions */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--t3)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 }}>
            Move to
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {nextStatuses.map((st) => {
              const meta = STATUS_META[st];
              return (
                <button
                  key={st}
                  onClick={() => setStatus(st)}
                  disabled={busy}
                  style={{
                    padding: "8px 14px",
                    background: meta.bg,
                    color: meta.color,
                    border: `1px solid ${meta.color}33`,
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: busy ? "wait" : "pointer",
                  }}
                >
                  {meta.label}
                </button>
              );
            })}
            {canEdit && script.status !== "draft" && (
              <button
                onClick={() => setStatus("draft")}
                disabled={busy}
                style={btnSecondary}
              >
                Move back to draft
              </button>
            )}
          </div>
        </div>

        {/* History */}
        {(script.history || []).length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--t3)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 }}>
              History
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {(script.history || []).slice().reverse().map((h, i) => (
                <div key={i} style={{ fontSize: 12, color: "var(--t3)", padding: "6px 0", borderBottom: "1px dashed var(--bdr)" }}>
                  <span style={{ color: "var(--t2)" }}>{h.by_name}</span>
                  {h.from ? (
                    <> · <span>{STATUS_META[h.from]?.label || h.from} → <strong style={{ color: "var(--t1)" }}>{STATUS_META[h.to]?.label || h.to}</strong></span></>
                  ) : (
                    <> · created as <strong style={{ color: "var(--t1)" }}>{STATUS_META[h.to]?.label || h.to}</strong></>
                  )}
                  <span style={{ marginLeft: 6 }}>· {fmtRelative(h.at)}</span>
                  {h.note && <div style={{ fontSize: 12, color: "var(--t2)", marginTop: 2, fontStyle: "italic" }}>"{h.note}"</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {canEdit && (
          <button
            onClick={del}
            disabled={busy}
            style={{
              padding: "8px 14px",
              background: "rgba(239,68,68,.08)",
              color: "#ef4444",
              border: "1px solid rgba(239,68,68,.3)",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              cursor: busy ? "wait" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Trash2 size={12} /> Delete
          </button>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════ Performance view ═══════════════════════ */

function PerformanceView({ data, myUid, expandedWriter, setExpandedWriter }) {
  const totals = data.totals || {};
  const rows = data.rows || [];

  if (rows.length === 0) {
    return (
      <EmptyState
        title="No submissions in this window"
        message="Once writers start submitting, daily counts will show up here."
      />
    );
  }

  return (
    <>
      {/* Totals strip */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        gap: 12,
        marginBottom: 24,
      }}>
        <TotalCell label="Submitted" value={totals.submitted ?? 0} />
        <TotalCell label="In review" value={totals.in_review ?? 0} accent="#f59e0b" />
        <TotalCell label="Approved" value={totals.approved ?? 0} accent="#22c55e" />
        <TotalCell label="Shelved" value={totals.shelved ?? 0} accent="#a78bfa" />
        <TotalCell label="Active writers" value={totals.active_writers ?? 0} />
      </div>

      {/* Per-writer table */}
      <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 14, overflow: "hidden" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "1.6fr 90px .7fr .7fr .7fr .8fr 18px",
          gap: 8,
          padding: "10px 16px",
          borderBottom: "1px solid var(--bdr)",
          fontSize: 11,
          color: "var(--t3)",
          fontWeight: 600,
          letterSpacing: ".04em",
          textTransform: "uppercase",
          background: "var(--s2)",
          minWidth: 600,
        }}>
          <div>Writer</div>
          <div>Trend</div>
          <div style={{ textAlign: "right" }}>Submitted</div>
          <div style={{ textAlign: "right" }}>In review</div>
          <div style={{ textAlign: "right" }}>Approved</div>
          <div style={{ textAlign: "right" }}>Approval %</div>
          <div></div>
        </div>
        {rows.map((r) => {
          const isMe = r.writer_id === myUid;
          const isExpanded = expandedWriter === r.writer_id;
          const series = r.daily_submissions_series || [];
          return (
            <React.Fragment key={r.writer_id}>
              <div
                onClick={() => setExpandedWriter(isExpanded ? null : r.writer_id)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.6fr 90px .7fr .7fr .7fr .8fr 18px",
                  gap: 8,
                  padding: "12px 16px",
                  borderBottom: "1px solid var(--bdr)",
                  background: isMe ? "rgba(96,165,250,.06)" : "transparent",
                  alignItems: "center",
                  cursor: "pointer",
                  minWidth: 600,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                  <AvatarChip name={r.name} size={26} />
                  <span style={{ fontSize: 14, fontWeight: isMe ? 600 : 500, color: "var(--t1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.name}{isMe ? " · you" : ""}
                  </span>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <Sparkline values={series} title={`${r.submitted} submitted · last ${series.length} days`} />
                </div>
                <div style={{ textAlign: "right", fontSize: 14, color: "var(--t1)", fontWeight: 600 }}>{r.submitted}</div>
                <div style={{ textAlign: "right", fontSize: 14, color: r.in_review > 0 ? "#f59e0b" : "var(--t3)" }}>{r.in_review}</div>
                <div style={{ textAlign: "right", fontSize: 14, color: r.approved > 0 ? "#22c55e" : "var(--t3)", fontWeight: r.approved > 0 ? 600 : 400 }}>{r.approved}</div>
                <div style={{ textAlign: "right", fontSize: 13, color: "var(--t2)" }}>
                  {r.approval_rate == null ? "—" : `${Math.round(r.approval_rate * 100)}%`}
                </div>
                <div style={{
                  color: "var(--t3)",
                  fontSize: 11,
                  transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "transform .15s",
                  textAlign: "center",
                }}>▶</div>
              </div>
              {isExpanded && (
                <DailyBreakdown rows={r.daily || []} totalDays={data.series_days} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
}

function TotalCell({ label, value, accent }) {
  return (
    <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: "14px 16px" }}>
      <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: accent || "var(--t1)", lineHeight: 1, letterSpacing: "-0.01em" }}>
        {value}
      </div>
    </div>
  );
}

function Sparkline({ values, color = "#60a5fa", title }) {
  if (!values || values.length === 0) return <span style={{ fontSize: 12, color: "var(--t3)" }}>—</span>;
  const max = Math.max(1, ...values);
  const w = 84, h = 22, gap = 1;
  const barW = Math.max(1, (w - gap * (values.length - 1)) / values.length);
  return (
    <svg width={w} height={h} style={{ display: "block" }} aria-label={title}>
      <title>{title}</title>
      {values.map((v, i) => {
        const bh = v === 0 ? 2 : Math.max(2, (v / max) * h);
        return (
          <rect
            key={i}
            x={i * (barW + gap)}
            y={h - bh}
            width={barW}
            height={bh}
            fill={v === 0 ? "var(--t4, #888)" : color}
            opacity={v === 0 ? 0.35 : 1}
            rx={1}
          />
        );
      })}
    </svg>
  );
}

function DailyBreakdown({ rows, totalDays }) {
  if (!rows || rows.length === 0) {
    return (
      <div style={{ padding: "14px 16px 18px 56px", borderBottom: "1px solid var(--bdr)", background: "var(--s2)", fontSize: 12, color: "var(--t3)" }}>
        No submissions in the last {totalDays} days.
      </div>
    );
  }
  return (
    <div style={{ padding: "10px 16px 14px 56px", borderBottom: "1px solid var(--bdr)", background: "var(--s2)" }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: "140px 100px",
        gap: 8,
        padding: "6px 0",
        fontSize: 10,
        color: "var(--t3)",
        fontWeight: 600,
        letterSpacing: ".04em",
        textTransform: "uppercase",
        borderBottom: "1px solid var(--bdr)",
      }}>
        <div>Date</div>
        <div style={{ textAlign: "right" }}>Submitted</div>
      </div>
      {rows.map((d) => (
        <div key={d.date} style={{
          display: "grid",
          gridTemplateColumns: "140px 100px",
          gap: 8,
          padding: "8px 0",
          fontSize: 13,
          color: "var(--t2)",
          borderBottom: "1px dashed var(--bdr)",
        }}>
          <div style={{ color: "var(--t1)", fontWeight: 500 }}>{fmtBreakdownDate(d.date)}</div>
          <div style={{ textAlign: "right", color: "var(--t1)", fontWeight: 600 }}>
            {d.submitted} {d.submitted === 1 ? "script" : "scripts"}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════ Helpers ═══════════════════════ */

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.round(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

function fmtBreakdownDate(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wk = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][dt.getUTCDay()];
  const mn = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1];
  return `${wk}, ${d} ${mn}`;
}
